import { computed, ComputedRef, effectScope } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import type { NavigationGuardNext, Route } from 'vue-router';
import {
  isMenuV2Enabled,
  isMenuV2Override,
  menuV2NewUntil,
  TMenuV2Override,
} from '@/domain/catalog/menu-v2-rollout';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { vueData } from '@/VueData';

/**
 * Menú V2 rollout (plan §5.1): the rule of `menu-v2-rollout.ts` plus a
 * per-device override set with `?menuV2=on|off|default` (the pattern of
 * `use-pos-v2.ts`).
 */
const override = useLocalStorage<TMenuV2Override>('menuV2Override', 'default');

function currentOverride(): TMenuV2Override {
  return isMenuV2Override(override.value) ? override.value : 'default';
}

/** Whether the Menú page replaces «Items» / «Categorías» for this store. */
export function menuV2EnabledFor(stringId: string | null): boolean {
  return isMenuV2Enabled({
    override: currentOverride(),
    isStaff: !!vueData.user?.admin,
    stringId,
  });
}

let isMenuV2Active: ComputedRef<boolean> | null = null;

export function useMenuV2() {
  if (!isMenuV2Active) {
    // Detached scope: the singleton computed outlives the component that
    // first asked for it (see use-pos-v2.ts).
    const scope = effectScope(true);
    isMenuV2Active = scope.run(() => {
      const restaurantStore = useRestaurantStore();
      return computed(() =>
        menuV2EnabledFor(restaurantStore.restaurant?.stringId ?? null)
      );
    })!;
  }
  return { isMenuV2Active, newUntil: menuV2NewUntil() };
}

type TQuery = Route['query'];

/**
 * Stores a `?menuV2=` override and returns the query without it; `stripped`
 * tells the guard to redirect to the clean URL.
 */
export function applyMenuV2QueryOverride(
  query: TQuery
): {
  query: TQuery;
  stripped: boolean;
} {
  if (!('menuV2' in query)) return { query, stripped: false };
  const value = query.menuV2;
  if (isMenuV2Override(value)) override.value = value;
  const rest = { ...query };
  delete rest.menuV2;
  return { query: rest, stripped: true };
}

function pick(query: TQuery, keys: readonly string[]): TQuery {
  const picked: TQuery = {};
  for (const key of keys)
    if (query[key] !== undefined) picked[key] = query[key];
  return picked;
}

/** `beforeEnter` of `/:restaurantId/menu`: flag off → the legacy «Items» page. */
export function requireMenuV2(
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): void {
  const { query, stripped } = applyMenuV2QueryOverride(to.query);
  if (stripped) {
    next({ path: to.path, query, hash: to.hash, replace: true });
    return;
  }
  const stringId = to.params.restaurantId;
  if (menuV2EnabledFor(stringId)) {
    next();
    return;
  }
  next({
    path: `/${stringId}/items`,
    query: pick(query, ['cId', 'mId']),
    replace: true,
  });
}

/**
 * `beforeEnter` of `/:restaurantId/items` and `/categories`: flag on → the
 * Menú page, keeping the menu (and the category from «Items»). The two
 * guards share one predicate, so they never loop.
 */
export function redirectToMenuV2(
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): void {
  const { query, stripped } = applyMenuV2QueryOverride(to.query);
  if (stripped) {
    next({ path: to.path, query, hash: to.hash, replace: true });
    return;
  }
  const stringId = to.params.restaurantId;
  if (!menuV2EnabledFor(stringId)) {
    next();
    return;
  }
  const keys = to.path.endsWith('/items') ? ['cId', 'mId'] : ['mId'];
  next({ path: `/${stringId}/menu`, query: pick(query, keys), replace: true });
}
